/*
 * Who has the app open, counted in windows rather than sessions.
 *
 * THERE IS NO SESSION TO MEASURE. This is a static page against a stateless
 * API - nobody logs out, nothing closes, and a phone can sit on the board for
 * an hour in a pocket. So "time spent" is not observed; it is inferred from the
 * one signal that already exists: the app refetches itself while it is visible,
 * every 30 seconds during a game and every 120 otherwise, and never while it is
 * hidden.
 *
 * Each of those refetches stamps a window. Time is the number of windows
 * somebody occupied, which makes it an OVER-estimate at the tail - a ten-second
 * glance marks a whole window - and roughly right for anything longer. The
 * admin screen says "about", because that is the truth.
 *
 * The window costs at most one row per player per window, so a phone left open
 * all afternoon writes thirty rows an hour rather than one per poll.
 */

/** Long enough that the slowest poll still lands in every window. */
export const BUCKET_SECONDS = 120

export function bucketOf(unixSeconds: number): number {
  return Math.floor(unixSeconds / BUCKET_SECONDS)
}

/** Occupied windows, as minutes. */
export function minutesFrom(buckets: number): number {
  return buckets * (BUCKET_SECONDS / 60)
}

/**
 * "now", "7m ago", "5h ago", "3d ago", "never".
 *
 * A future timestamp is a clock disagreement between a phone and the server,
 * not a visit that has not happened yet, so it reads as "now" - "in 4 minutes"
 * on an admin screen looks like a bug and invites someone to chase it.
 */
export function lastSeenText(seenAt: number | null, now: number): string {
  if (seenAt === null) return 'never'
  const d = Math.max(0, now - seenAt)
  if (d < 90) return 'now'
  if (d < 3600) return `${Math.round(d / 60)}m ago`
  if (d < 86_400) return `${Math.round(d / 3600)}h ago`
  return `${Math.round(d / 86_400)}d ago`
}
